import { liveQuery } from 'dexie';
import { LiveValue, NetworkError, ProblemError, type ConnectionState, type HttpClient, type SyncService, type Update, type RealtimeSignal } from '@/model';
import { decodeConversation, decodeAccount, decodeSnapshot, decodeUpdate, decodeUpdates, object } from './decode';
import { capabilities, initialProtocol } from './protocol';
import { admissionFor } from './capabilities';
import { applyMutableReceipt } from './resource-receipts';
import { AccountStore, applySnapshot, applyUpdates, fenceStore, importConversation, revokeConversationAccess, type ApplyResult, type SyncMeta } from './store';

const localResyncLocks = new Map<string, Promise<void>>();
async function withResyncLock(name: string, run: () => Promise<void>): Promise<void> {
  if (globalThis.navigator?.locks) return navigator.locks.request(name, run);
  const previous = localResyncLocks.get(name) ?? Promise.resolve();
  let release!: () => void; const gate = new Promise<void>(resolve => { release = resolve; });
  const tail = previous.catch(() => undefined).then(() => gate); localResyncLocks.set(name, tail);
  await previous.catch(() => undefined);
  try { await run(); } finally { release(); if (localResyncLocks.get(name) === tail) localResyncLocks.delete(name); }
}

/** The only socket test seam: the transport edge. Store and update application remain real. */
export interface StreamSocket {
  readyState: number;
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number }) => void) | null;
  onerror: (() => void) | null;
  send(value: string): void;
  close(code?: number): void;
}
interface SyncOptions {
  store: AccountStore; http: HttpClient; deviceId: string;
  accessToken(): Promise<string>;
  refresh(): Promise<void>;
  protocol?: () => number; baseUrl?: string;
  socket?: (url: string, protocols: string[]) => StreamSocket;
  onReady?: () => void;
  onSignal?: (signal: Record<string, unknown>) => void;
}
export class SyncEngine implements SyncService {
  readonly hasSnapshot = new LiveValue(false);
  private readonly signalListeners = new Set<{ types: readonly string[] | '*'; handler: (signal: RealtimeSignal) => void }>();
  onSignal(types: readonly string[] | '*', handler: (signal: RealtimeSignal) => void) { const entry = { types, handler }; this.signalListeners.add(entry); return () => { this.signalListeners.delete(entry); }; }
  readonly connection = new LiveValue<ConnectionState>({ phase: 'idle', since: Date.now() });
  private running = false;
  private lifetime = 0;
  private socketId = 0;
  private socket?: StreamSocket;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private ackTimer?: ReturnType<typeof setTimeout>;
  private lastAckAt = 0;
  private attempts = 0;
  private authRejections = 0;
  private queue: Promise<void> = Promise.resolve();
  private resnapshot?: Promise<void>;
  private connecting?: Promise<void>;
  private replicaGeneration?: number;
  private generationRecovery?: Promise<void>;
  private abort?: AbortController;
  private repairSubscription?: { unsubscribe(): void };
  private repairTimer?: ReturnType<typeof setTimeout>;
  private readonly fetching = new Set<string>();
  private readonly listeners = new Set<{ types: readonly string[] | '*'; handler: (update: Update) => void }>();
  constructor(private readonly options: SyncOptions) {}
  private phase(phase: ConnectionState['phase'], retryAt?: number) { this.connection.set({ phase, since: Date.now(), ...(retryAt !== undefined ? { retryAt } : {}) }); }
  private current(lifetime: number) { return this.running && lifetime === this.lifetime; }
  private committed(meta?: SyncMeta): meta is SyncMeta { return Boolean(meta?.stream_epoch && (meta.snapshot_generation === undefined || meta.snapshot_generation === meta.generation) && meta.protocol === (this.options.protocol?.() ?? initialProtocol)); }
  private closeSocket() {
    this.socketId++; const socket = this.socket; this.socket = undefined;
    if (socket) admissionFor(this.options.http)?.beginConnection();
    if (socket) { socket.onopen = null; socket.onmessage = null; socket.onclose = null; socket.onerror = null; socket.close(1000); }
    if (this.ackTimer) clearTimeout(this.ackTimer); this.ackTimer = undefined; this.lastAckAt = 0;
  }
  onUpdate(types: readonly string[] | '*', handler: (update: Update) => void) { const listener = { types, handler }; this.listeners.add(listener); return () => { this.listeners.delete(listener); }; }
  private publish(result: ApplyResult) {
    for (const u of result.applied) for (const listener of this.listeners) if (listener.types === '*' || listener.types.includes(u.type)) { try { listener.handler(u); } catch { /* One consumer cannot stop the durable cursor. */ } }
  }
  async start(): Promise<void> {
    if (this.running) return;
    this.running = true; this.lifetime++; this.abort = new AbortController();
    globalThis.addEventListener?.('online', this.wake);
    globalThis.document?.addEventListener('visibilitychange', this.visible);
    const meta = await this.options.store.meta.get('sync');
    try {
      if (!this.committed(meta)) await this.resync();
      else { this.hasSnapshot.set(true); await this.openSocket(); }
    } catch { if (this.running) this.offline(); }
    if (this.running) this.repairSubscription = liveQuery(async () => ({ meta: await this.options.store.meta.get('sync'), rows: await this.options.store.fetches.count() + await this.options.store.resourceFetches.count() })).subscribe({
      next: value => {
        if (!this.running || this.resnapshot || !value.meta) return;
        if (value.meta.generation !== this.replicaGeneration || this.committed(value.meta) && !this.hasSnapshot.get()) void this.recoverGeneration().catch(() => this.offline());
        else if (value.rows && this.committed(value.meta)) void this.fetchMissing(value.meta.generation, this.lifetime);
      }, error: () => undefined,
    });
  }
  stop(): void {
    this.running = false; this.lifetime++; this.abort?.abort(); this.closeSocket();
    this.repairSubscription?.unsubscribe(); this.repairSubscription = undefined;
    if (this.repairTimer) clearTimeout(this.repairTimer); this.repairTimer = undefined;
    if (this.retryTimer) clearTimeout(this.retryTimer); this.retryTimer = undefined;
    globalThis.removeEventListener?.('online', this.wake);
    globalThis.document?.removeEventListener('visibilitychange', this.visible);
    this.hasSnapshot.set(false); this.phase('idle');
  }
  private async connect(): Promise<void> {
    const meta = await this.options.store.meta.get('sync');
    if (!this.running) return;
    if (!this.committed(meta)) await this.resync();
    else { this.hasSnapshot.set(true); await this.openSocket(); }
  }
  private visible = () => { if (document.visibilityState === 'visible') this.wake(); };
  reconnect(): void { this.wake(); }
  private wake = () => {
    if (!this.running || this.resnapshot) return;
    if (this.retryTimer) clearTimeout(this.retryTimer); this.retryTimer = undefined;
    this.closeSocket(); this.attempts = 0;
    void this.connect().catch(() => { if (this.running) this.offline(); });
  };
  private offline() {
    if (!this.running || this.retryTimer) return;
    this.closeSocket();
    const delay = Math.min(30000, 500 * 2 ** Math.min(this.attempts++, 6)) * (.75 + Math.random() * .5);
    this.phase('offline', Date.now() + delay);
    this.retryTimer = setTimeout(() => { this.retryTimer = undefined; void this.connect().catch(() => this.offline()); }, delay);
  }
  async resync(adoptGeneration?: number): Promise<void> {
    if (this.resnapshot) return this.resnapshot;
    this.closeSocket(); this.abort?.abort();
    if (this.retryTimer) clearTimeout(this.retryTimer); this.retryTimer = undefined;
    const lifetime = ++this.lifetime;
    const abort = new AbortController(); this.abort = abort;
    this.hasSnapshot.set(false); this.phase('resyncing');
    let recover = false;
    this.resnapshot = (async () => {
      const expected = await this.options.store.meta.get('sync');
      await withResyncLock(`hylo.web.sync:${this.options.store.name}`, async () => {
        if (!this.current(lifetime)) return;
        const held = await this.options.store.meta.get('sync');
        // A tab that waited for another snapshot consumes its commit instead of immediately replacing it.
        if (this.committed(held) && (held.generation !== expected?.generation || !this.committed(expected) || adoptGeneration !== undefined && held.generation >= adoptGeneration)) return;
        const fence = await fenceStore(this.options.store); this.replicaGeneration = fence.generation;
        const protocol = this.options.protocol?.() ?? initialProtocol;
        const s = decodeSnapshot(await this.options.http.get('/v1/sync/snapshot', { signal: abort.signal, protocol, cause: 'refresh' }));
        if (!this.current(lifetime)) return;
        await applySnapshot(this.options.store, s, { generation: fence.generation, protocol });
      });
      if (!this.current(lifetime)) return;
      const committed = await this.options.store.meta.get('sync');
      if (this.committed(committed)) { this.replicaGeneration = committed.generation; this.hasSnapshot.set(true); await this.openSocket(); }
      else recover = true;
    })().catch(error => { if (this.current(lifetime)) this.offline(); throw error; }).finally(() => {
      this.resnapshot = undefined;
      if (recover && this.current(lifetime)) queueMicrotask(() => { void this.recoverGeneration().catch(() => this.offline()); });
    });
    return this.resnapshot;
  }
  /** A generation fence is shared across tabs. Retire old work, then join the committed generation. */
  private async recoverGeneration(): Promise<void> {
    if (!this.running || this.resnapshot) return;
    if (this.generationRecovery) return this.generationRecovery;
    const lifetime = this.lifetime;
    this.generationRecovery = (async () => {
      const meta = await this.options.store.meta.get('sync');
      if (!this.current(lifetime) || this.resnapshot) return;
      this.lifetime++; this.abort?.abort(); this.abort = new AbortController(); this.closeSocket();
      if (this.retryTimer) clearTimeout(this.retryTimer); this.retryTimer = undefined;
      const generation = meta?.generation;
      this.replicaGeneration = generation; this.hasSnapshot.set(false);
      if (this.committed(meta)) { this.hasSnapshot.set(true); await this.openSocket(); }
      else await this.resync(generation);
    })().finally(() => { this.generationRecovery = undefined; });
    return this.generationRecovery;
  }
  private async openSocket(): Promise<void> {
    if (!this.running) return;
    // A superseded connection task must not suppress opening the new generation after a resync.
    const lifetime = this.lifetime;
    if (this.connecting) { await this.connecting; if (!this.current(lifetime)) return; }
    this.connecting = (async () => {
      const meta = await this.options.store.meta.get('sync');
      if (!this.committed(meta) || !this.current(lifetime)) return;
      const token = await this.options.accessToken();
      if (!this.current(lifetime)) return;
      const held = await this.options.store.meta.get('sync');
      if (!this.current(lifetime)) return;
      if (!this.committed(held) || held.generation !== meta.generation) { queueMicrotask(() => { void this.recoverGeneration().catch(() => this.offline()); }); return; }
      this.replicaGeneration = meta.generation;
      this.closeSocket(); const socketId = this.socketId;
      const admission = admissionFor(this.options.http); const admissionEpoch = admission?.beginConnection();
      this.phase('connecting');
      const base = this.options.baseUrl ?? globalThis.location?.origin ?? 'http://localhost';
      const url = new URL('/v1/stream', base); url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      const socket = (this.options.socket ?? ((u, p) => new WebSocket(u, p) as unknown as StreamSocket))(url.toString(), ['hylo.v1', `hylo.bearer.${token}`]);
      this.socket = socket;
      const valid = () => this.current(lifetime) && socketId === this.socketId && this.socket === socket;
      socket.onopen = () => { if (!valid()) return; this.phase('syncing'); socket.send(JSON.stringify({ t: 'hello', after: meta.pts, device_id: this.options.deviceId, protocol: this.options.protocol?.() ?? initialProtocol, stream_epoch: meta.stream_epoch, capabilities })); };
      socket.onmessage = event => {
        if (!valid()) return;
        this.queue = this.queue.then(async () => {
          if (!valid()) return;
          let frame: Record<string, unknown>;
          try { frame = object(JSON.parse(String(event.data))); } catch { await this.resync(); return; }
          if (frame.t === 'update') await this.update(decodeUpdate(frame), meta.generation, lifetime);
          else if (frame.t === 'ready') {
            const cursor = await this.options.store.meta.get('sync');
            if (!valid()) return;
            if (cursor?.generation !== meta.generation) { await this.recoverGeneration(); return; }
            if (!cursor || frame.stream_epoch !== cursor.stream_epoch) { await this.resync(); return; }
            if (Number(frame.pts) > cursor.pts) await this.catchUp(meta.generation, lifetime);
            if (!valid()) return;
            if (admissionEpoch !== undefined) admission?.ready(frame.capabilities, admissionEpoch);
            this.attempts = 0; this.authRejections = 0; this.phase('online'); await this.ack(true); this.options.onReady?.(); void this.fetchMissing(meta.generation, lifetime);
          } else if (frame.t === 'signal' && typeof frame.type === 'string') {
            const cursor = await this.options.store.meta.get('sync');
            if (!valid()) return;
            if (cursor?.generation !== meta.generation) { await this.recoverGeneration(); return; }
            const signal = frame as RealtimeSignal;
            this.options.onSignal?.(signal);
            for (const listener of this.signalListeners) if (listener.types === '*' || listener.types.includes(signal.type)) { try { listener.handler(signal); } catch { /* Isolate feature consumers. */ } }
          }
          else if (frame.t === 'resync') await this.resync();
          else if (frame.t === 'error') this.offline();
        }).catch(() => { if (valid()) this.offline(); });
      };
      socket.onclose = event => {
        if (!valid()) return;
        if (event.code === 4409) void this.resync().catch(() => undefined);
        else if (event.code === 4401) {
          this.closeSocket();
          // A revoked family must reach refresh's authoritative rejection; a stream cannot silently loop forever.
          this.authRejections++;
          void this.options.refresh().then(() => { if (this.current(lifetime)) { if (this.authRejections > 1) this.offline(); else return this.openSocket(); } }).catch(() => { if (this.current(lifetime)) this.offline(); });
        } else this.offline();
      };
      socket.onerror = () => { if (valid()) this.offline(); };
    })().finally(() => { this.connecting = undefined; });
    await this.connecting;
  }
  private async update(u: Update, generation: number, lifetime: number): Promise<void> {
    if (!this.current(lifetime)) return;
    let result = await applyUpdates(this.options.store, [u], generation); if (this.current(lifetime)) this.publish(result);
    if (result.stop === 'fenced') { await this.recoverGeneration(); return; }
    if (result.stop === 'unknown') { await this.resync(); return; }
    if (result.stop === 'gap') {
      const wasOnline = this.connection.get().phase === 'online';
      await this.catchUp(generation, lifetime);
      if (wasOnline && this.current(lifetime)) this.phase('online');
      if (!this.current(lifetime)) return;
      result = await applyUpdates(this.options.store, [u], generation); if (this.current(lifetime)) this.publish(result);
      if (result.stop === 'fenced') { await this.recoverGeneration(); return; }
      if (result.stop === 'unknown' || result.stop === 'gap') { await this.resync(); return; }
    }
    if (result.stop !== 'fenced' && this.current(lifetime)) { await this.ack(); void this.fetchMissing(generation, lifetime); }
  }
  private async catchUp(generation: number, lifetime: number): Promise<void> {
    this.phase('syncing');
    while (this.current(lifetime)) {
      const before = await this.options.store.meta.get('sync'); if (!before || before.generation !== generation) { await this.recoverGeneration(); return; }
      try {
        const page = decodeUpdates(await this.options.http.get('/v1/updates', { query: { after: before.pts, limit: 500 }, cause: 'background', signal: this.abort?.signal }));
        if (!this.current(lifetime)) return;
        if (page.stream_epoch !== before.stream_epoch) { await this.resync(); return; }
        const result = await applyUpdates(this.options.store, page.updates, generation); if (this.current(lifetime)) this.publish(result);
        if (result.stop === 'unknown' || result.stop === 'gap') { await this.resync(); return; }
        if (result.stop === 'fenced') { await this.recoverGeneration(); return; }
        if (!page.has_more) return;
        if (result.pts <= before.pts) throw new NetworkError('Catch-up made no progress');
      } catch (error) {
        if (error instanceof ProblemError && error.status === 410) { await this.resync(); return; }
        throw error;
      }
    }
  }
  private async fetchMissing(generation: number, lifetime: number): Promise<void> {
    if (!this.current(lifetime) || this.resnapshot) return;
    try {
      for (const { conversation_id } of await this.options.store.fetches.toArray()) {
        if (!this.current(lifetime)) return;
        if (this.fetching.has(conversation_id)) continue;
        const before = await this.options.store.meta.get('sync');
        if (!this.current(lifetime) || before?.generation !== generation) return;
        this.fetching.add(conversation_id);
        try {
          const response = object(await this.options.http.get(`/v1/conversations/${conversation_id}`, { cause: 'update', signal: this.abort?.signal }));
          let stale = false;
          await this.options.store.transaction('rw', this.options.store.tables, async () => {
            const cursor = await this.options.store.meta.get('sync');
            if (!this.current(lifetime) || cursor?.generation !== generation) return;
            if (cursor.pts !== before?.pts) { stale = true; return; }
            await importConversation(this.options.store, decodeConversation(response.conversation), Array.isArray(response.accounts) ? response.accounts.map(decodeAccount) : [], false, true);
          });
          if (stale && !this.repairTimer) this.repairTimer = setTimeout(() => { this.repairTimer = undefined; void this.fetchMissing(generation, lifetime); }, 100);
        } catch (error) {
          if (this.current(lifetime) && error instanceof ProblemError && (error.status === 403 || error.status === 404)) {
            let stale = false;
            await this.options.store.transaction('rw', this.options.store.tables, async () => {
              const cursor = await this.options.store.meta.get('sync');
              if (!this.current(lifetime) || cursor?.generation !== generation) return;
              if (cursor.pts !== before.pts) { stale = true; return; }
              await revokeConversationAccess(this.options.store, conversation_id);
            });
            if (stale && !this.repairTimer) this.repairTimer = setTimeout(() => { this.repairTimer = undefined; void this.fetchMissing(generation, lifetime); }, 100);
          }
          // Transient repairs stay queued for the next update, ready, or reconnect.
        } finally { this.fetching.delete(conversation_id); }
      }
      await this.fetchResources(generation, lifetime);
    } catch { /* A stopped or unavailable store has no repair work. */ }
  }
  private async fetchResources(generation: number, lifetime: number): Promise<void> {
    const db = this.options.store;
    for (const { id } of await db.resourceFetches.toArray()) {
      const key = `resource:${id}`; if (this.fetching.has(key)) continue;
      const before = await db.meta.get('sync');
      if (!this.current(lifetime) || before?.generation !== generation) return;
      this.fetching.add(key);
      let stale = false;
      try {
        const path = id === 'me' ? '/v1/me' : id === 'pins' ? '/v1/pins' : `/v1/accounts/${encodeURIComponent(id.slice('account:'.length))}`;
        const response = object(await this.options.http.get(path, { cause: 'update', signal: this.abort?.signal }));
        await db.transaction('rw', db.tables, async () => {
          const cursor = await db.meta.get('sync');
          if (!this.current(lifetime) || cursor?.generation !== generation) return;
          stale = cursor.pts !== before.pts || id === 'pins' && (cursor.pins_revision ?? 0) !== (before.pins_revision ?? 0);
          await applyMutableReceipt(db, response, !stale, before.pins_revision ?? 0);
          if (!stale) await db.resourceFetches.delete(id);
        });
      } catch { /* Keep durable repairs for the next ready, update, focus or reconnect. */ }
      finally { this.fetching.delete(key); }
      if (stale && !this.repairTimer) this.repairTimer = setTimeout(() => { this.repairTimer = undefined; void this.fetchMissing(generation, lifetime); }, 100);
    }
  }
  private async ack(force = false): Promise<void> {
    const socket = this.socket; const socketId = this.socketId;
    if (!socket || socket.readyState !== 1) return;
    const elapsed = Date.now() - this.lastAckAt;
    if (!force && elapsed < 1000) {
      if (!this.ackTimer) this.ackTimer = setTimeout(() => { this.ackTimer = undefined; void this.ack(); }, 1000 - elapsed);
      return;
    }
    const meta = await this.options.store.meta.get('sync');
    if (!this.committed(meta) || meta.generation !== this.replicaGeneration || socket !== this.socket || socketId !== this.socketId || socket.readyState !== 1) return;
    socket.send(JSON.stringify({ t: 'ack', pts: meta.pts })); this.lastAckAt = Date.now();
  }
  sendTyping(conversationId: string, state: 'start' | 'stop'): void {
    if (this.socket?.readyState === 1 && this.connection.get().phase === 'online') this.socket.send(JSON.stringify({ t: 'typing', conversation_id: conversationId, state }));
  }
}
